import React, { useMemo } from 'react';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import { Button, Dropdown, Menu } from 'antd';
import { MoreOutlined, PlusOutlined, EnvironmentOutlined } from '@ant-design/icons';

import { getGoogleMapsCoordsUrl } from '../../helpers/maps';

const formatSignatureDateTime = ({
  timestamp,
  timezone,
  showTime,
  showDate,
}) => {
  if (
    !timestamp
    || (!showTime && !showDate)
  ) return null;

  const sigDt = timezone
    ? DateTime.fromMillis(timestamp, { zone: timezone })
    : DateTime.fromMillis(timestamp);

  const viewerZone = DateTime.local().zoneName;
  const showTz = timezone && timezone !== viewerZone;
  let formatted;
  if (showDate && showTime) {
    formatted = sigDt.toLocaleString(DateTime.DATETIME_MED);
  } else if (showTime) {
    formatted = sigDt.toLocaleString(DateTime.TIME_SIMPLE);
  } else {
    formatted = sigDt.toLocaleString(DateTime.DATE_MED);
  }

  if (showTz) formatted = `${formatted} ${sigDt.toFormat('ZZZZ')}`;
  return formatted;
};

export default function SignatureTile({
  signature,
  onClick,
  onDelete,
  onClear,
  isDisplay,
  showDate = true,
  showSignatureTime,
  collectGeolocation,
  hideName = false,
}) {
  const {
    id,
    sig,
    name,
    timestamp,
    timezone,
    latitude,
    longitude,
  } = signature ?? {};

  const {
    url,
  } = sig ?? {};

  const signatureTs = id && sig ? timestamp : null;

  const signatureText = useMemo(() => (
    formatSignatureDateTime({
      timestamp: signatureTs,
      timezone,
      showTime: showSignatureTime,
      showDate,
    })
  ), [signatureTs, timezone, showSignatureTime]);

  const geoText = useMemo(() => {
    if (!collectGeolocation || !sig) return null;
    if (latitude && longitude) {
      return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`; // accurate up to 10cm
    }
    return 'Location could not be captured';
  }, [collectGeolocation, sig, latitude, longitude]);

  const mapHref = useMemo(() => (
    getGoogleMapsCoordsUrl(latitude, longitude)
  ), [latitude, longitude]);

  const handleClick = () => {
    if (onClick) onClick(signature);
  };

  const handleDelete = () => {
    if (onDelete) onDelete(id);
  };

  const handleClear = () => {
    if (onClear) onClear(id);
  };

  const noSigView = isDisplay ? (
    <div className="signature-tile-image-container" />
  ) : (
    <Button
      className="form-attachment-add-button-redesign"
      style={{ height: 89.4, width: 173.5, backgroundColor: 'white' }}
      onClick={onClick ? handleClick : undefined}
    >
      <div className="form-attachment-add-button-center">
        <div className="file-tile-download-icon">
          <PlusOutlined />
        </div>
        <span>
          Add signature
        </span>
      </div>
    </Button>
  );

  const showMenu = !isDisplay && (onDelete || (sig && onClear));

  const menuOverlay = (
    <Menu>
      {sig && onClear && (
        <Menu.Item key="clear" onClick={handleClear}>
          Clear
        </Menu.Item>
      )}
      {onDelete && (
        <Menu.Item key="delete" danger onClick={handleDelete}>
          Delete
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <div className="signature-tile">
      {sig ? (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className="signature-tile-image-container"
          onClick={handleClick}
          onKeyDown={(e) => e.key === 'Enter' && handleClick()}
          style={{ cursor: onClick ? 'pointer' : 'default' }}
        >
          <img
            className="signature-tile-image"
            alt={`${name}'s signature`}
            src={sig instanceof File ? URL.createObjectURL(sig) : url}
          />
        </div>
      ) : (
        noSigView
      )}
      <div className="signature-tile-bottom-row">
        <div className="signature-tile-text-group">
          {!hideName && (
            <div className="signature-tile-name">
              {name ?? 'Unknown'}
            </div>
          )}
          {signatureText && (
            <div className="signature-tile-signed">
              {`Signed ${signatureText}`}
            </div>
          )}
          {geoText && (
            <div className="signature-tile-geo-row">
              <div className="signature-tile-geo">
                <EnvironmentOutlined style={{ fontSize: 10, marginRight: 3 }} />
                <span>{geoText}</span>
              </div>
              {mapHref && (
                <a
                  className="signature-tile-geo-map-link"
                  href={mapHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View map
                </a>
              )}
            </div>
          )}
        </div>
        {showMenu && (
          <Dropdown
            overlay={menuOverlay}
            trigger={['click']}
            placement="bottomRight"
            overlayStyle={{
              borderRadius: 8,
              overflow: 'hidden',
              border: '1px solid #E5E7EB',
            }}
          >
            <div className="signature-tile-menu-icon">
              <MoreOutlined />
            </div>
          </Dropdown>
        )}
      </div>
    </div>
  );
}

SignatureTile.propTypes = {
  signature: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sig: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    name: PropTypes.string,
    url: PropTypes.string,
    timestamp: PropTypes.number,
    timezone: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
  }),
  onClick: PropTypes.func,
  onDelete: PropTypes.func,
  onClear: PropTypes.func,
  isDisplay: PropTypes.bool,
  showSignatureTime: PropTypes.bool,
  collectGeolocation: PropTypes.bool,
  showDate: PropTypes.bool,
  hideName: PropTypes.bool,
};

SignatureTile.defaultProps = {
  signature: null,
  onClick: null,
  onDelete: null,
  onClear: null,
  isDisplay: false,
  showSignatureTime: false,
  collectGeolocation: false,
  showDate: true,
  hideName: false,
};
